import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogBody,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { PaperAirplaneIcon } from '@heroicons/react/24/outline';

interface ConfirmationModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  onConfirm: () => void;
  title: string;
  description?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  icon?: React.ReactNode;
  children?: React.ReactNode;
  confirmDisabled?: boolean;
  variant?: 'default' | 'danger' | 'warning';
  /**
   * Tone the ICON BADGE ONLY, leaving the confirm button on `variant`.
   *
   * `variant` deliberately couples the two — a destructive dialog wants a red
   * badge AND a red button. But a surface can legitimately need the badge to
   * carry attention while the action stays ordinary: the consent attestation
   * asks a careful question, yet "Import 8 contacts" is the happy path and an
   * amber button would misread it as the risky one.
   *
   * Passing an amber `icon` with the default variant does NOT achieve that —
   * the badge's BACKGROUND still comes from `variant`, so an amber glyph lands
   * on a sage circle and reads as a discoloured icon rather than a warning.
   *
   * Defaults to `variant`, so every existing call site is unchanged.
   */
  iconVariant?: 'default' | 'danger' | 'warning';
  autoClose?: boolean;
  closeDisabled?: boolean;
}

export default function ConfirmationModal({
  open,
  setOpen,
  onConfirm,
  title,
  description,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  icon,
  children,
  confirmDisabled = false,
  variant = 'default',
  iconVariant,
  autoClose = true,
  closeDisabled = false,
}: ConfirmationModalProps) {
  const handleConfirm = () => {
    onConfirm();
    if (autoClose) setOpen(false);
  };

  const isDanger = variant === 'danger';
  const isWarning = variant === 'warning';

  // The badge follows `iconVariant` when given, else `variant` — so the default
  // is the old coupled behaviour and no existing call site moves.
  const badge = iconVariant ?? variant;

  // The warning variant is TOKEN-based (status-amber-*), deliberately NOT
  // another raw-hex variant like default/danger — those inline hexes are a
  // DESIGN.md anti-pattern we're not compounding. Amber (attention) over red
  // (destructive): the actions that use it are recoverable.
  let iconBgClass = 'bg-sage-light';
  let iconTextClass = 'text-sage';

  if (badge === 'danger') {
    iconBgClass = 'bg-status-red-bg';
    iconTextClass = 'text-status-red-text';
  } else if (badge === 'warning') {
    iconBgClass = 'bg-status-amber-bg';
    iconTextClass = 'text-status-amber-text';
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen && closeDisabled) return;
        setOpen(nextOpen);
      }}
    >
      <DialogContent
        className="flex flex-col overflow-hidden w-[calc(100vw-32px)] max-w-[500px] max-h-[calc(100dvh-32px)] overflow-hidden rounded-lg"
        onEscapeKeyDown={(event) => closeDisabled && event.preventDefault()}
        onPointerDownOutside={(event) => closeDisabled && event.preventDefault()}
      >
        <DialogBody className="space-y-4">
          <div className="flex items-start">
            <div
              className={`flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full ${iconBgClass} sm:mx-0 sm:h-10 sm:w-10`}
            >
              {icon || <PaperAirplaneIcon className={`size-6 ${iconTextClass}`} />}
            </div>

            <div className="ml-3 mt-0 min-w-0 flex-grow sm:ml-4">
              <DialogHeader>
                {/* Centered against the 40px (sm:h-10) icon badge, so the title
                    reads as being ON the same line as the icon rather than
                    top-aligned above it.

                    Wraps ONLY the title. `sm:items-center` on the outer row would
                    centre the whole right column — title AND body — against the
                    badge, which collapses on any modal with a tall body. The body
                    is a sibling of DialogHeader and stays top-anchored. */}
                <div className="flex min-h-10 items-center">
                  <DialogTitle className="text-lg text-ink-secondary">{title}</DialogTitle>
                </div>
              </DialogHeader>
              <div className="mt-2">
                {description && <p className="text-sm text-ink-muted">{description}</p>}
                {children}
              </div>
            </div>
          </div>
        </DialogBody>

        <DialogFooter>
          <Button
            type="button"
            variant="neutral"
            size="workspace"
            onClick={() => setOpen(false)}
            disabled={closeDisabled}
          >
            {cancelLabel}
          </Button>

          <Button
            type="button"
            variant={isDanger ? 'dangerConfirm' : isWarning ? 'custom' : 'confirm'}
            size="workspace"
            onClick={handleConfirm}
            disabled={confirmDisabled}
            className={
              isWarning
                ? 'border border-status-amber-text/55 bg-status-amber-bg text-status-amber-text hover:bg-status-amber-bg/80'
                : undefined
            }
          >
            {confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
